// Mercapoli - Favorites Page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Clock, Heart, Loader2 } from 'lucide-react';
import CustomerLayout from '../../layouts/CustomerLayout';
import { Badge } from '../../components/ui/badge';
import { favoritos as favoritosApi } from '../../lib/api';
import { toast } from 'sonner';

const Favorites = () => {
  const [loading, setLoading] = useState(true);
  const [favoritos, setFavoritos] = useState([]);

  useEffect(() => {
    loadFavoritos();
  }, []);

  const loadFavoritos = async () => {
    try {
      const data = await favoritosApi.getAll();
      setFavoritos(data);
    } catch (error) {
      console.error('Error cargando favoritos:', error);
    }
    setLoading(false);
  };

  const removeFavorito = async (restauranteId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await favoritosApi.remove(restauranteId);
      setFavoritos(prev => prev.filter(r => r.restaurante_id !== restauranteId));
      toast.success('Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al eliminar de favoritos');
    }
  };

  return (
    <CustomerLayout title="Mis favoritos" showSearch={false}>
      <div className="container-app pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
          </div>
        ) : favoritos.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Heart className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-bold mb-2">No tienes favoritos</h3>
            <p className="text-muted-foreground mb-4">
              Guarda tus restaurantes favoritos para acceder rápidamente
            </p>
            <Link to="/app">
              <button className="text-spicy-coral font-medium hover:underline">
                Explorar restaurantes
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {favoritos.map((restaurante, index) => (
              <motion.div
                key={restaurante.restaurante_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link 
                  to={`/app/restaurante/${restaurante.restaurante_id}`}
                  className="group block"
                >
                  <div className="bg-card rounded-2xl overflow-hidden border border-border/50 card-hover">
                    {/* Imagen */}
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={restaurante.imagen}
                        alt={restaurante.nombre}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Remove favorite button */}
                      <button
                        onClick={(e) => removeFavorito(restaurante.restaurante_id, e)}
                        className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors"
                      >
                        <Heart className="h-5 w-5 fill-spicy-coral text-spicy-coral" />
                      </button>

                      {/* Status badge */}
                      {restaurante.abierto ? (
                        <Badge className="absolute top-3 left-3 bg-zesty-lime text-black">
                          Abierto
                        </Badge>
                      ) : (
                        <Badge className="absolute top-3 left-3 bg-gray-500">
                          Cerrado
                        </Badge>
                      )}

                      {/* Info overlay */}
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="font-heading text-lg font-bold text-white mb-1">
                          {restaurante.nombre}
                        </h3>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="font-medium">{restaurante.calificacion}</span>
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{restaurante.tiempo_preparacion_min} min</span>
                          </div>
                        </div>
                        <span className="text-spicy-coral font-medium">
                          ${restaurante.costo_envio?.toFixed(0)} envío
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default Favorites;
